import { datadogRum } from "@datadog/browser-rum";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { FieldValues, FormProvider, SubmitHandler, UseFormReturn, useForm } from "react-hook-form";
import { z } from "zod";

import { Headline } from "@fronterahealth/frontera-ui-components";

import { FormDynamicScoreTables } from "@components/AssessmentFormRenderer/components/FormDynamicScoreTables";
import { FormGroupedRows } from "@components/AssessmentFormRenderer/components/FormGroupedRows";
import { FormKeyValueList } from "@components/AssessmentFormRenderer/components/FormKeyValueList";
import { FormLikertItems } from "@components/AssessmentFormRenderer/components/FormLikertItems";
import { FormScoreTable } from "@components/AssessmentFormRenderer/components/FormScoreTable";
import { FormSingleSelect } from "@components/AssessmentFormRenderer/components/FormSingleSelect";
import { FormSkillChecklist } from "@components/AssessmentFormRenderer/components/FormSkillChecklist";
import { FormTextField } from "@components/AssessmentFormRenderer/components/FormTextField";
import { AssessmentLayoutConfig } from "@components/AssessmentFormRenderer/types/layoutTypes";
import { restoreNullForUntouchedNestedObjects } from "@components/AssessmentFormRenderer/utils/restoreNullForUntouchedNestedObjects";

export interface UseAssessmentFormProps<TFieldValues extends FieldValues = FieldValues> {
  schema: z.ZodSchema<TFieldValues, TFieldValues>;
  values: TFieldValues;
  onSave: (data: TFieldValues) => void | Promise<void>;
}

export interface UseAssessmentFormReturn<TFieldValues extends FieldValues = FieldValues> {
  methods: UseFormReturn<TFieldValues>;
  submit: () => void;
  isDirty: boolean;
  isSubmitting: boolean;
  error: Error | null;
}

export const useAssessmentForm = <TFieldValues extends FieldValues = FieldValues>({
  schema,
  values,
  onSave,
}: UseAssessmentFormProps<TFieldValues>): UseAssessmentFormReturn<TFieldValues> => {
  const [error, setError] = useState<Error | null>(null);

  const methods = useForm<TFieldValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    shouldFocusError: false,
    values,
  });

  const { handleSubmit, reset, formState } = methods;

  const onHandleSubmit: SubmitHandler<TFieldValues> = async (data) => {
    if (!formState.isDirty) return;
    const cleaned = restoreNullForUntouchedNestedObjects(data, values);
    await onSave(cleaned);
    reset(cleaned);
  };

  const safeSubmit = async () => {
    try {
      await handleSubmit(onHandleSubmit)();
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      err.name = "AssessmentFormError";
      datadogRum.addError(err, { type: "assessmentFormSubmitCrash" });
      setError(err);
    }
  };

  return {
    methods,
    submit: safeSubmit,
    isDirty: formState.isDirty,
    isSubmitting: formState.isSubmitting,
    error,
  };
};

export interface AssessmentFormRendererProps<TFieldValues extends FieldValues = FieldValues> {
  layout: AssessmentLayoutConfig;
  methods: UseFormReturn<TFieldValues>;
  onSubmit: () => void;
  error?: Error | null;
}

export const AssessmentFormRenderer = <TFieldValues extends FieldValues = FieldValues>({
  layout,
  methods,
  onSubmit,
  error,
}: AssessmentFormRendererProps<TFieldValues>) => {
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <Headline size="small">Document review unavailable</Headline>
        <p className="text-sm text-gray-500">
          An error occurred while loading the review form. The document preview is still available.
        </p>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        className="flex flex-col gap-6"
      >
        {layout.sections.map((section) => (
          <div key={section.label}>
            {section.type === "text_field" && <FormTextField section={section} />}
            {section.type === "score_table" && <FormScoreTable section={section} />}
            {section.type === "single_select" && <FormSingleSelect section={section} />}
            {section.type === "likert_items" && <FormLikertItems section={section} />}
            {section.type === "grouped_rows" && <FormGroupedRows section={section} />}
            {section.type === "key_value_list" && <FormKeyValueList section={section} />}
            {section.type === "dynamic_score_tables" && <FormDynamicScoreTables section={section} />}
            {section.type === "skill_checklist" && <FormSkillChecklist section={section} />}
          </div>
        ))}
      </form>
    </FormProvider>
  );
};
